import { ApiError, api } from '../../lib/api'
import { activateStrings as A, strings as S } from './strings'

/**
 * Signup endpoints — `Features/Auth/Signup/Endpoint.cs`.
 *
 *   getSignupPage -> GET  /api/signup?id={invitationId}
 *                    -> { IsArabic, DefaultApplicationTitle, CanSignup, ErrorMessage,
 *                         InvitationId, InvitationEmail }
 *   signup        -> POST /Signup/Register
 *                    { InvitationId, NameAr, NameEn, Username, Email, Password }
 *                    -> { Result, Message, NextStep, ErrorCode }
 *
 * TWO WAYS IN, and the screen has to tell them apart before it renders:
 *
 *   by invitation  `/Signup?id={guid}` from an invitation email. The email is fixed by
 *                  the invitation and shown read-only, and the link expires.
 *   open           `/Signup` typed or reached from the login page, allowed only when
 *                  `ShowRegistrationLinkOnLoginPage` is on.
 *
 * Either way registration can be CLOSED — expired invitation, disabled signup, no active
 * licence — and the server says so with `CanSignup: false` plus a reason. That is a
 * successful response, not an error: the Razor handler rendered the shared error view for
 * it, and the React screen renders the same message in the auth layout.
 */

/** SignupPageViewModel -> the shape the screen consumes. */
function toPageState(dto) {
  return {
    canSignup: Boolean(dto.CanSignup),
    // Shown as received (W6) — the server has already localized it.
    errorMessage: dto.ErrorMessage ?? '',
    appTitle: dto.DefaultApplicationTitle ?? '',
    invitation: dto.InvitationId
      ? { id: dto.InvitationId, email: dto.InvitationEmail ?? '' }
      : null,
  }
}

/**
 * @param {string|null} invitationId  the `id` in the URL, when the visitor came from an
 *                                    invitation email
 */
export async function getSignupPage(invitationId, signal) {
  const query = invitationId ? `?id=${encodeURIComponent(invitationId)}` : ''
  return toPageState((await api.get(`/api/signup${query}`, { signal })) ?? {})
}

/**
 * What a successful registration SAYS, when the server did not say it itself.
 *
 * The old fallback was one sentence — "the account was created" — for all three
 * outcomes, which is only true of the first. `NextStep` is the server's own
 * verdict on what happens next, and creating an account that cannot be signed
 * into until an email is confirmed, or until an administrator approves it, is
 * not the same news: a visitor told only "created" goes to the login form and
 * finds it refuses them.
 *
 * The wording is the ACTIVATE screens' own — the same two sentences the visitor
 * is about to land on, and byte-identical to the server's
 * `SignupVerifyEmailMessage` / `SignupAdminApprovalMessage`, so nothing new is
 * invented here (W6). A message the server DOES send still wins over all of it.
 */
const NEXT_STEP_MESSAGE = {
  User: A.states.waitingUser,
  Admin: A.states.waitingAdmin,
}

export async function signup({ invitationId, nameAr, nameEn, email, username, password }) {
  try {
    const data = await api.post('/Signup/Register', {
      // An absent invitation is omitted, not sent empty: the command takes a `Guid?` and
      // the handler branches on `HasValue`.
      InvitationId: invitationId || null,
      NameAr: nameAr,
      NameEn: nameEn,
      Username: username,
      Email: email,
      Password: password,
    })

    /* "Login" | "User" (verify by email) | "Admin" (await approval) — the handler picks
       it from the company's activation settings, and the screen routes on it. */
    const nextStep = data?.NextStep || 'Login'

    return {
      Result: true,
      /* `||`, not `??`: an empty string is not a message. It passes a nullish check
         and would put an EMPTY dialog on screen — a success the reader cannot tell
         from a rendering fault. */
      Message: data?.Message || NEXT_STEP_MESSAGE[nextStep] || S.messages.accountCreated,
      NextStep: nextStep,
    }
  } catch (error) {
    return { Result: false, Message: messageOf(error), NextStep: null }
  }
}

/**
 * A refusal from the handler is shown in the server's own words — it is the only thing
 * that knows whether the username is taken, the password too weak or the licence full.
 */
function messageOf(error) {
  if (error instanceof ApiError && (error.code === 'RESULT' || error.code === 'PROBLEM') && error.message) {
    return error.message
  }
  return S.messages.systemError
}
